import React, { useEffect, useState, useMemo } from 'react';
import { Plus, Search, Calendar, MapPin, ChevronDown, ChevronUp, Activity, Clock, Film, CheckCircle2 } from 'lucide-react';
import Layout from '../components/Layout';
import { workflowAPI } from '../utils/api';
import { toast } from 'sonner';

const ProjectRegister = ({ embedded }) => {
  const [projects, setProjects] = useState([]);
  const [projectEvents, setProjectEvents] = useState({}); // { project_id: [events] }
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState('all');
  const [collapsedYears, setCollapsedYears] = useState({});
  const [statusFilter, setStatusFilter] = useState(null); // 'in_progress' | 'Pending Data' | 'With Editors' | 'Ready to Close' | null
  const [formData, setFormData] = useState({
    bride_name: '',
    groom_name: '',
    venue: '',
    planner_name: '',
    planner_contact: '',
    wedding_start_date: '',
    final_delivery_date: ''
  });

  useEffect(() => {
    initializeData();
  }, []);

  const initializeData = async () => {
    // Silent auto-sync so admins don't have to remember to click a button
    try { await workflowAPI.syncFromScheduling(); } catch { /* silent — not critical */ }
    await Promise.all([fetchProjects(), fetchStats(), fetchAllEvents()]);
    setLoading(false);
  };

  const fetchProjects = async () => {
    try {
      const response = await workflowAPI.getProjects();
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await workflowAPI.getDashboardStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchAllEvents = async () => {
    try {
      const response = await workflowAPI.getEvents();
      const grouped = (response.data || []).reduce((acc, ev) => {
        (acc[ev.project_id] = acc[ev.project_id] || []).push(ev);
        return acc;
      }, {});
      setProjectEvents(grouped);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await workflowAPI.createProject(formData);
      setShowForm(false);
      setFormData({ bride_name: '', groom_name: '', venue: '', planner_name: '', planner_contact: '', wedding_start_date: '', final_delivery_date: '' });
      toast.success('Project created successfully');
      await Promise.all([fetchProjects(), fetchStats(), fetchAllEvents()]);
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Error creating project');
    }
  };

  const getDueDateStatus = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date();
    const due = new Date(dueDate);
    const diffDays = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { color: 'text-red-700 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-500/10', label: `${Math.abs(diffDays)} days overdue` };
    if (diffDays <= 7) return { color: 'text-amber-700 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-500/10', label: `Due in ${diffDays} days` };
    return { color: 'text-emerald-700 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-500/10', label: `Due in ${diffDays} days` };
  };

  // Aggregate per-project workflow status: which event-status statuses does this project have?
  const projectStatusMap = useMemo(() => {
    const map = {};
    for (const p of projects) {
      const evs = projectEvents[p.id] || [];
      const statuses = new Set(evs.map(e => e.overall_status).filter(Boolean));
      map[p.id] = statuses;
    }
    return map;
  }, [projects, projectEvents]);

  const todayISOForFilter = new Date().toISOString().slice(0, 10);

  const passesStatusFilter = (project) => {
    if (!statusFilter) return true;
    if (statusFilter === 'in_progress') {
      const start = project.wedding_start_date;
      const end = project.wedding_end_date || start;
      return start && end && start <= todayISOForFilter && todayISOForFilter <= end;
    }
    return (projectStatusMap[project.id] || new Set()).has(statusFilter);
  };

  const filteredProjects = projects.filter(p =>
    (
      (p.bride_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.groom_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.project_code || '').toLowerCase().includes(searchTerm.toLowerCase())
    ) && passesStatusFilter(p)
  );

  // Client-side compute of "In Progress" count (project-level, based on wedding date window)
  const inProgressCount = useMemo(
    () => projects.filter(p => {
      const s = p.wedding_start_date; const e = p.wedding_end_date || s;
      return s && e && s <= todayISOForFilter && todayISOForFilter <= e;
    }).length,
    [projects, todayISOForFilter],
  );

  // Group projects by year based on wedding_start_date
  const getProjectYear = (project) => {
    if (!project.wedding_start_date) return 'No Date';
    return new Date(project.wedding_start_date).getFullYear().toString();
  };

  // Year priority: current year first, future ascending, past descending, "No Date" last
  const currentYear = new Date().getFullYear();
  const yearSortKey = (y) => {
    if (y === 'No Date') return [3, 0];
    const n = Number(y);
    if (n === currentYear) return [0, 0];
    if (n > currentYear) return [1, n];
    return [2, -n];
  };
  const compareYears = (a, b) => {
    const [ga, ka] = yearSortKey(a);
    const [gb, kb] = yearSortKey(b);
    if (ga !== gb) return ga - gb;
    return ka - kb;
  };

  // Within-year sort: upcoming/in-progress first, then most-recently-past.
  const todayISO = new Date().toISOString().slice(0, 10);
  const compareProjectsByDate = (a, b) => {
    const aStart = a.wedding_start_date || '';
    const aEnd = a.wedding_end_date || aStart;
    const bStart = b.wedding_start_date || '';
    const bEnd = b.wedding_end_date || bStart;
    if (!aStart && !bStart) return 0;
    if (!aStart) return 1;
    if (!bStart) return -1;
    const aActive = aEnd >= todayISO;
    const bActive = bEnd >= todayISO;
    if (aActive && !bActive) return -1;
    if (!aActive && bActive) return 1;
    if (aActive) return aStart.localeCompare(bStart);
    return bEnd.localeCompare(aEnd);
  };

  const getAvailableYears = () => {
    const years = [...new Set(filteredProjects.map(getProjectYear))].filter(y => y !== 'No Date');
    years.sort(compareYears);
    if (filteredProjects.some(p => !p.wedding_start_date)) {
      years.push('No Date');
    }
    return years;
  };

  const filteredByYear = selectedYear === 'all' 
    ? filteredProjects 
    : filteredProjects.filter(p => getProjectYear(p) === selectedYear);

  const projectsByYear = filteredByYear.reduce((acc, project) => {
    const year = getProjectYear(project);
    if (!acc[year]) acc[year] = [];
    acc[year].push(project);
    return acc;
  }, {});

  // Sort within each year
  Object.keys(projectsByYear).forEach(y => {
    projectsByYear[y].sort(compareProjectsByDate);
  });

  const sortedYears = Object.keys(projectsByYear).sort(compareYears);

  const toggleYearCollapse = (year) => {
    setCollapsedYears(prev => ({ ...prev, [year]: !prev[year] }));
  };

  const Wrap = embedded ? React.Fragment : Layout;

  if (loading) {
    return (
      <Wrap>
        <div className="flex items-center justify-center h-96">
          <div className="animate-pulse text-muted-foreground font-serif">Loading...</div>
        </div>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <div className="p-6 lg:p-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-serif tracking-tight" data-testid="workflow-projects-title">Workflow Projects</h1>
            <p className="text-muted-foreground mt-1">{projects.length} total projects</p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground hover:bg-black/90 transition-all text-sm uppercase tracking-wide font-medium"
            data-testid="add-project-button"
          >
            <Plus className="h-5 w-5" />
            {showForm ? 'Cancel' : 'Add Project'}
          </button>
        </div>

        {/* Status pill strip — click to filter */}
        <div className="flex flex-wrap gap-2 mb-5" data-testid="workflow-status-pills">
          <StatusPill
            icon={Activity}
            label="In Progress"
            count={inProgressCount}
            active={statusFilter === 'in_progress'}
            onClick={() => setStatusFilter(statusFilter === 'in_progress' ? null : 'in_progress')}
            colorClass="text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30 hover:bg-emerald-100 dark:bg-emerald-500/15"
            activeColorClass="bg-emerald-600 text-white border-emerald-600"
            testid="pill-in-progress"
          />
          <StatusPill
            icon={Clock}
            label="Pending Data"
            count={stats?.pending_data || 0}
            active={statusFilter === 'Pending Data'}
            onClick={() => setStatusFilter(statusFilter === 'Pending Data' ? null : 'Pending Data')}
            colorClass="text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30 hover:bg-amber-100 dark:bg-amber-500/15"
            activeColorClass="bg-amber-600 text-white border-amber-600"
            testid="pill-pending-data"
          />
          <StatusPill
            icon={Film}
            label="With Editors"
            count={stats?.with_editors || 0}
            active={statusFilter === 'With Editors'}
            onClick={() => setStatusFilter(statusFilter === 'With Editors' ? null : 'With Editors')}
            colorClass="text-sky-700 dark:text-sky-400 bg-sky-50 dark:bg-sky-500/10 border-sky-200 dark:border-sky-500/30 hover:bg-sky-100 dark:bg-sky-500/15"
            activeColorClass="bg-sky-600 text-white border-sky-600"
            testid="pill-with-editors"
          />
          <StatusPill
            icon={CheckCircle2}
            label="Ready to Close"
            count={stats?.ready_to_close || 0}
            active={statusFilter === 'Ready to Close'}
            onClick={() => setStatusFilter(statusFilter === 'Ready to Close' ? null : 'Ready to Close')}
            colorClass="text-purple-700 dark:text-purple-400 bg-purple-50 dark:bg-purple-500/10 border-purple-200 dark:border-purple-500/30 hover:bg-purple-100 dark:bg-purple-500/15"
            activeColorClass="bg-purple-600 text-white border-purple-600"
            testid="pill-ready-to-close"
          />
          {statusFilter && (
            <button
              onClick={() => setStatusFilter(null)}
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 ml-1 self-center"
              data-testid="pill-clear-filter"
            >
              Clear filter
            </button>
          )}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-input bg-background text-sm focus:outline-none focus:ring-1 focus:ring-gold"
              data-testid="search-projects"
            />
          </div>
          
          {/* Year Filter */}
          {getAvailableYears().length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Year:</span>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="px-3 py-2.5 border border-input bg-background text-sm"
                data-testid="year-filter"
              >
                <option value="all">All Years</option>
                {getAvailableYears().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {showForm && (
          <div className="bg-card border border-border p-6 mb-6">
            <h2 className="text-xl font-serif mb-4">Add New Project</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input type="text" placeholder="Bride Name *" required value={formData.bride_name} onChange={(e) => setFormData({ ...formData, bride_name: e.target.value })} className="px-4 py-3 border border-input bg-background text-sm" data-testid="bride-name-input" />
                <input type="text" placeholder="Groom Name *" required value={formData.groom_name} onChange={(e) => setFormData({ ...formData, groom_name: e.target.value })} className="px-4 py-3 border border-input bg-background text-sm" data-testid="groom-name-input" />
                <input type="text" placeholder="Venue *" required value={formData.venue} onChange={(e) => setFormData({ ...formData, venue: e.target.value })} className="px-4 py-3 border border-input bg-background text-sm" data-testid="venue-input" />
                <div>
                  <label className="block text-xs uppercase tracking-wide mb-1">Wedding Date *</label>
                  <input type="date" required value={formData.wedding_start_date} onChange={(e) => setFormData({ ...formData, wedding_start_date: e.target.value })} className="w-full px-4 py-3 border border-input bg-background text-sm" data-testid="wedding-date-input" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wide mb-1">Final Delivery Date</label>
                  <input type="date" value={formData.final_delivery_date} onChange={(e) => setFormData({ ...formData, final_delivery_date: e.target.value })} className="w-full px-4 py-3 border border-input bg-background text-sm" data-testid="delivery-date-input" />
                  <p className="text-xs text-muted-foreground mt-1">When should everything be finished?</p>
                </div>
                <input type="text" placeholder="Planner Name" value={formData.planner_name} onChange={(e) => setFormData({ ...formData, planner_name: e.target.value })} className="px-4 py-3 border border-input bg-background text-sm" />
                <input type="text" placeholder="Planner Contact" value={formData.planner_contact} onChange={(e) => setFormData({ ...formData, planner_contact: e.target.value })} className="px-4 py-3 border border-input bg-background text-sm" />
              </div>
              <button type="submit" className="w-full px-6 py-3 bg-primary text-primary-foreground hover:bg-black/90 transition-all text-sm uppercase tracking-wide font-medium" data-testid="submit-project">
                Create Project
              </button>
            </form>
          </div>
        )}

        <div className="space-y-6">
          {sortedYears.length === 0 ? (
            <div className="bg-card border border-border p-12 text-center">
              <p className="text-muted-foreground mb-4">No projects found</p>
              <button onClick={() => setShowForm(true)} className="text-gold font-medium hover:underline">
                Create your first project
              </button>
            </div>
          ) : (
            sortedYears.map(year => (
              <div key={year} className="border border-border" data-testid={`year-group-${year}`}>
                {/* Year Header */}
                <button
                  onClick={() => toggleYearCollapse(year)}
                  className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl font-serif">{year}</span>
                    <span className="text-sm text-muted-foreground">
                      ({projectsByYear[year].length} project{projectsByYear[year].length !== 1 ? 's' : ''})
                    </span>
                  </div>
                  {collapsedYears[year] ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
                </button>
                
                {/* Projects in this year */}
                {!collapsedYears[year] && (
                  <div className="divide-y divide-border">
                    {projectsByYear[year].map((project) => (
                      <div key={project.id} className="p-6 hover:bg-accent/30 transition-colors" data-testid="project-card">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                              <span className="text-xs font-bold text-gold bg-gold/10 px-2 py-1">{project.project_code}</span>
                              <span className={`px-2.5 py-0.5 text-xs font-medium ${project.status === 'active' ? 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-800 dark:text-emerald-300' : 'bg-secondary text-foreground'}`}>
                                {project.status}
                              </span>
                              {(() => {
                                const start = project.wedding_start_date;
                                const end = project.wedding_end_date || start;
                                if (start && end && start <= todayISO && todayISO <= end) {
                                  return (
                                    <span
                                      className="inline-flex items-center gap-1 text-xs px-2 py-0.5 border uppercase tracking-wide bg-emerald-50 dark:bg-emerald-500/10 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30"
                                      data-testid={`wf-in-progress-${project.id}`}
                                    >
                                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                                      In Progress
                                    </span>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                            <h3 className="text-xl font-serif mb-2">{project.bride_name} & {project.groom_name}</h3>
                            <div className="space-y-1 text-sm text-muted-foreground">
                              <div className="flex items-center gap-2"><MapPin className="h-4 w-4" />{project.venue}</div>
                              <div className="flex items-center gap-2"><Calendar className="h-4 w-4" />Event: {project.wedding_start_date}</div>
                              {project.final_delivery_date && (() => {
                                const status = getDueDateStatus(project.final_delivery_date);
                                return status ? (
                                  <div className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium ${status.bg} ${status.color}`}>
                                    {status.label}
                                  </div>
                                ) : null;
                              })()}
                            </div>
                          </div>
                          <a href={`/workflow?tab=events&project=${project.id}`} className="px-4 py-2 bg-primary text-primary-foreground hover:bg-black/90 transition-all text-sm uppercase tracking-wide font-medium text-center" data-testid="view-events-button">
                            View Events
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </Wrap>
  );
};

const StatusPill = ({ icon: Icon, label, count, active, onClick, colorClass, activeColorClass, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-2 px-3 py-1.5 border text-sm font-medium transition-all ${active ? activeColorClass : colorClass}`}
    data-testid={testid}
    aria-pressed={active}
  >
    <Icon size={14} />
    <span>{label}</span>
    <span className={`text-xs font-mono px-1.5 py-0.5 ${active ? 'bg-card/20' : 'bg-card/60'}`}>{count}</span>
  </button>
);

export default ProjectRegister;
